<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  color: {
    type: String,
    default: '#3b82f6',
  },
  height: {
    type: Number,
    default: 40,
  },
});

const svgRef = ref(null);

// Helper to safely extract numeric value from data point
const extractValue = d => {
  if (d === null || d === undefined) return 0;
  if (typeof d === 'number') return Number.isFinite(d) ? d : 0;
  const val = d.value ?? d.count ?? 0;
  return Number.isFinite(val) ? val : 0;
};

// Helper to check if we have valid data
const hasValidData = computed(() => {
  return props.data && Array.isArray(props.data) && props.data.length > 0;
});

const points = computed(() => {
  if (!hasValidData.value) return '';

  const values = props.data.map(extractValue);

  // Ensure we have at least one non-zero finite value
  if (values.every(v => v === 0)) {
    // Return a flat line at the middle if all values are zero
    const width = 100;
    const height = props.height;
    const padding = 2;
    const midY = height / 2;
    return values
      .map((_, index) => {
        const x =
          (index / (values.length - 1 || 1)) * (width - padding * 2) + padding;
        return `${x.toFixed(2)},${midY.toFixed(2)}`;
      })
      .join(' ');
  }

  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  const width = 100;
  const height = props.height;
  const padding = 2;

  return values
    .map((value, index) => {
      const x =
        (index / (values.length - 1 || 1)) * (width - padding * 2) + padding;
      const y =
        height - padding - ((value - min) / range) * (height - padding * 2);
      // Ensure we always return finite numbers
      const safeX = Number.isFinite(x) ? x.toFixed(2) : padding.toFixed(2);
      const safeY = Number.isFinite(y) ? y.toFixed(2) : (height / 2).toFixed(2);
      return `${safeX},${safeY}`;
    })
    .join(' ');
});

const areaPoints = computed(() => {
  if (!hasValidData.value) return '';

  const values = props.data.map(extractValue);

  // Handle all-zero case
  if (values.every(v => v === 0)) {
    const width = 100;
    const height = props.height;
    const padding = 2;
    const midY = height / 2;
    const linePoints = values.map((_, index) => {
      const x =
        (index / (values.length - 1 || 1)) * (width - padding * 2) + padding;
      return `${x.toFixed(2)},${midY.toFixed(2)}`;
    });
    return `${padding},${(height - padding).toFixed(2)} ${linePoints.join(' ')} ${(width - padding).toFixed(2)},${(height - padding).toFixed(2)}`;
  }

  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  const width = 100;
  const height = props.height;
  const padding = 2;

  const linePoints = values.map((value, index) => {
    const x =
      (index / (values.length - 1 || 1)) * (width - padding * 2) + padding;
    const y =
      height - padding - ((value - min) / range) * (height - padding * 2);
    // Ensure we always return finite numbers
    const safeX = Number.isFinite(x) ? x.toFixed(2) : padding.toFixed(2);
    const safeY = Number.isFinite(y) ? y.toFixed(2) : (height / 2).toFixed(2);
    return `${safeX},${safeY}`;
  });

  // Close the path for the area
  return `${padding},${(height - padding).toFixed(2)} ${linePoints.join(' ')} ${(width - padding).toFixed(2)},${(height - padding).toFixed(2)}`;
});

const gradientId = computed(
  () => `sparkline-gradient-${Math.random().toString(36).substr(2, 9)}`
);

// Compute the last Y position safely for the end dot
const lastPointY = computed(() => {
  if (!points.value || points.value.length === 0) return props.height / 2;
  const lastPoint = points.value.split(' ').pop();
  if (!lastPoint) return props.height / 2;
  const yPart = lastPoint.split(',')[1];
  const y = parseFloat(yPart);
  return Number.isFinite(y) ? y : props.height / 2;
});
</script>

<template>
  <svg
    ref="svgRef"
    :viewBox="`0 0 100 ${height}`"
    preserveAspectRatio="none"
    class="w-full h-full"
  >
    <defs>
      <linearGradient :id="gradientId" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" :stop-color="color" stop-opacity="0.3" />
        <stop offset="100%" :stop-color="color" stop-opacity="0" />
      </linearGradient>
    </defs>

    <!-- Area fill -->
    <polygon
      v-if="hasValidData && areaPoints"
      :points="areaPoints"
      :fill="`url(#${gradientId})`"
    />

    <!-- Line -->
    <polyline
      v-if="hasValidData && points"
      :points="points"
      fill="none"
      :stroke="color"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />

    <!-- End dot -->
    <circle
      v-if="hasValidData && points"
      :cx="98"
      :cy="lastPointY"
      r="3"
      :fill="color"
    />
  </svg>
</template>
